import { Avatar, Box, MenuItem, Select, Typography, makeStyles } from "@material-ui/core";
import dayjs from "dayjs";
import { useSnackbar } from "notistack";
import * as React from "react";

import {
  Agent,
  InvestmentProposalPreferredContactSchedulesRequest,
  InvestmentProposalPreferredContactSchedulesRequestFirstPreferredContactTimeSlotEnum,
  InvestmentProposalPreferredContactSchedulesRequestSecondPreferredContactTimeSlotEnum,
} from "@/api/generated";
import { useGetAgentAvailableContactSlots } from "@/hooks/useGetAgentAvailableContactSlots";
import theme from "@/theme";
import {
  PreferredContactTimeSlot,
  buildPreferredContactDateOptions,
  findPreferredContactTimeSlotOptions,
} from "@/utilities/preferredContactSchedules";

import MatchingCheckImage from "@assets/image/matching_check.png";
import NoImage from "@assets/image/NoImage.svg";
import RecommendAgentImage from "@assets/image/recommend_agent.png";
import ShakeHandsImage from "@assets/image/shakehands.png";

import { PlannerApprovalPresentation } from "../agent/PlannerApprovalPresentation";
import { CustomButton } from "../Button";
import { StyledModal } from "../StyledModal";

// マッチ成立後の希望連絡日程を入力できるオファー種別（入力は任意でスキップ可）。
// 三井住友インシュアランス連携（msins_*）は先方が日程調整するため対象外
const PREFERRED_CONTACT_SCHEDULE_OFFER_TYPES = ["investment", "user_offer", "assessment"] as const;

// 送信に失敗しても無反応だと「押したのに何も起きなかった」に見えるため必ずフィードバックを出す。
// サーバがエラー応答を返した場合は保存もメール送信も行われておらず、保存成功時はメール失敗も
// API側でrescueされ200が返るため、その範囲では再送でメールは重複しない（再試行を促してよい）。
// ただしサーバ処理完了後の通信断などでクライアントだけ失敗表示になった場合の再送では、
// 保存は同値上書きで無害だがメールだけ再送されうる（まれな経路として許容）
const PREFERRED_CONTACT_SCHEDULES_ERROR_MESSAGE = "希望日程の送信に失敗しました。もう一度お試しください。";

// 対象条件（＝ユーザーからのマッチに対する認定プランナー側の承認条件）に合致しないマッチで出す案内。
// 日程フォームは表示せず、プランナーの承諾後に日程調整の連絡があることを案内する。
export const OUT_OF_TARGET_CONDITION_TITLE = "マッチ申請を送信しました";
export const OUT_OF_TARGET_CONDITION_DESCRIPTION =
  "認定プランナーがマッチを承諾した場合、認定プランナーより日程調整のご連絡をいたします。";
export const OUT_OF_TARGET_CONDITION_SUBMIT_LABEL = "完了";

const useStyles = makeStyles(() => ({
  messageContent: {
    display: "flex",
    alignItems: "center",
    flexDirection: "column",
    padding: "20px 20px 20px 20px",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: 30,
    marginTop: theme.spacing(2),
  },
  button: {
    minWidth: 100,
  },
  description: {
    textAlign: "center",
    fontSize: 14,
    fontWeight: 400,
    lineHeight: 1.5,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  investmentDescription: {
    textAlign: "left",
    marginBottom: theme.spacing(0),
  },
  investmentDescriptionNote: {
    width: "100%",
    color: theme.palette.text.secondary,
    fontSize: 12,
    fontWeight: 400,
    lineHeight: 1.5,
  },
  link: {
    textDecoration: "none",
    cursor: "pointer",
    textAlign: "center",
    "&:hover": {
      borderBottom: "none",
    },
  },
  image: {
    width: 150,
    height: "auto",
  },
  title: {
    color: theme.palette.primary.main,
    fontSize: 16,
    fontWeight: 700,
    lineHeight: 1.3,
  },
  scheduleForm: {
    width: "100%",
  },
  scheduleFieldGroup: {
    width: "100%",
    marginTop: theme.spacing(2),
  },
  scheduleLabel: {
    color: theme.palette.primary.main,
    fontSize: 12,
    fontWeight: 700,
    lineHeight: 1.3,
    marginBottom: theme.spacing(1),
  },
  scheduleRow: {
    display: "flex",
    gap: theme.spacing(1),
  },
  scheduleSelect: {
    flex: 1,
    height: 42,
    borderRadius: 4,
    backgroundColor: theme.palette.common.white,
    fontSize: 12,
    "& .MuiSelect-select": {
      padding: "12px 28px 12px 10px",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.grey[300],
    },
  },
  submitButtonWrap: {
    width: "100%",
    marginTop: theme.spacing(2),
  },
  skipButtonContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: 24,
    marginTop: theme.spacing(1),
  },
  noteText: {
    width: "100%",
    fontSize: 12,
    fontWeight: 400,
    lineHeight: 1.5,
    marginTop: theme.spacing(1),
  },
  recommendationSection: {
    width: "100%",
  },
  recommendationImage: {
    display: "block",
    width: "100%",
    maxWidth: 300,
    height: "auto",
    margin: `0 auto ${theme.spacing(2)}px`,
  },
  recommendationTitle: {
    color: theme.palette.primary.main,
    textAlign: "center",
    marginBottom: theme.spacing(1),
    fontSize: 14,
    fontWeight: 700,
    lineHeight: 1.3,
  },
  recommendationTopText: {
    textAlign: "center",
    fontSize: 12,
    fontWeight: 400,
    lineHeight: 1.5,
    marginBottom: theme.spacing(1),
  },
  recommendationList: {
    display: "flex",
    gap: theme.spacing(1.5),
    overflowX: "auto",
    paddingBottom: theme.spacing(1),
  },
  recommendationCard: {
    width: 92,
    minWidth: 92,
    maxWidth: 92,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    flexShrink: 0,
  },
  recommendationProfileWrap: {
    position: "relative",
    width: 92,
    height: 92,
    marginBottom: theme.spacing(1),
  },
  recommendationProfileBgCircle: {
    position: "absolute",
    top: 0,
    left: 0,
    width: 92,
    height: 92,
    borderRadius: "50%",
    backgroundColor: theme.palette.common.white,
    border: `2px solid ${theme.palette.primary.main}`,
    boxSizing: "border-box",
  },
  recommendationAvatar: {
    position: "absolute",
    top: 6,
    left: 6,
    width: 80,
    height: 80,
  },
  recommendationNameOneLine: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    width: "100%",
    display: "block",
    textAlign: "center",
  },
  recommendationCompany: {
    fontSize: 14,
    fontWeight: 500,
    lineHeight: 1.4,
  },
  recommendationName: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    fontWeight: 400,
    lineHeight: 1.4,
  },
  recommendationButton: {
    marginTop: theme.spacing(0.75),
    width: "100%",
  },
  recommendationBottomText: {
    color: theme.palette.text.secondary,
    fontSize: 10,
    fontWeight: 400,
    lineHeight: 1.5,
  },
}));

interface props {
  variant?: "legacy" | "planner";
  offerType: "investment" | "assessment" | "user_offer" | "msins_own" | "msins_consideration";
  onClose: () => void;
  additionalAgents?: Agent[];
  onClickAdditionalAgent?: (agent: Agent) => void;
  onSubmitPreferredContactSchedules?: (request: InvestmentProposalPreferredContactSchedulesRequest) => Promise<boolean>;
  // 希望日程の選択肢をこの認定プランナーの対応可能枠に絞るために使う。
  // 未指定なら制限なし（全枠選択可）として扱う
  agentId?: number;
  // 対象条件に合致しなかった（＝プランナーの承諾待ちになる）マッチかどうか。
  // 判定できなかった場合（API が is_target を返さないなど）は undefined のままにして、
  // 従来どおり日程フォームを出す側へ倒す
  isOutOfTargetCondition?: boolean;
}
export const OfferApproveModal: React.FC<props> = ({
  onClose,
  variant = "legacy",
  offerType,
  additionalAgents = [],
  onClickAdditionalAgent,
  onSubmitPreferredContactSchedules,
  agentId,
  isOutOfTargetCondition,
}) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const isScheduleOfferType = (PREFERRED_CONTACT_SCHEDULE_OFFER_TYPES as readonly string[]).includes(offerType);
  // 対象外と確定したときだけ案内へ切り替える（undefined は判定不能なので従来どおり日程フォーム）
  const isOutOfTargetGuidanceVisible = isScheduleOfferType && isOutOfTargetCondition === true;
  const isPreferredContactScheduleVisible = isScheduleOfferType && !isOutOfTargetGuidanceVisible;
  // 対応可能枠は取得できなくても全枠選択可へ倒れる（フック側で fail-open 済み）
  const { availableContactSlots, loading: loadingAvailableContactSlots } = useGetAgentAvailableContactSlots(
    isPreferredContactScheduleVisible ? agentId : undefined
  );
  // 取得中は制限なしの候補を出しつつ送信だけ止める。
  // 取得完了後に候補が狭まっても、下の useEffect が選択値を候補内へ寄せ直す
  const dateOptions = React.useMemo(
    () => buildPreferredContactDateOptions(dayjs(), availableContactSlots),
    [availableContactSlots]
  );
  const selectMenuProps = React.useMemo(
    () => ({
      style: {
        zIndex: 1402,
      },
    }),
    []
  );
  const [firstPreferredDate, setFirstPreferredDate] = React.useState(dateOptions[0]?.value ?? "");
  const [firstPreferredTimeSlot, setFirstPreferredTimeSlot] = React.useState<PreferredContactTimeSlot | "">(
    dateOptions[0]?.timeSlots[0]?.value ?? ""
  );
  const [secondPreferredDate, setSecondPreferredDate] = React.useState(
    dateOptions[1]?.value ?? dateOptions[0]?.value ?? ""
  );
  const [secondPreferredTimeSlot, setSecondPreferredTimeSlot] = React.useState<PreferredContactTimeSlot | "">(
    (dateOptions[1] ?? dateOptions[0])?.timeSlots[0]?.value ?? ""
  );
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const submittingRef = React.useRef(false);
  const mountedRef = React.useRef(true);
  React.useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  const [isRecommendationOnlyVisible, setIsRecommendationOnlyVisible] = React.useState(false);
  const firstTimeSlotOptions = React.useMemo(
    () => findPreferredContactTimeSlotOptions(dateOptions, firstPreferredDate),
    [dateOptions, firstPreferredDate]
  );
  const secondTimeSlotOptions = React.useMemo(
    () => findPreferredContactTimeSlotOptions(dateOptions, secondPreferredDate),
    [dateOptions, secondPreferredDate]
  );
  const canSubmitPreferredContactSchedules =
    !loadingAvailableContactSlots &&
    !!firstPreferredTimeSlot &&
    !!secondPreferredTimeSlot &&
    firstTimeSlotOptions.length > 0 &&
    secondTimeSlotOptions.length > 0;

  // 対応可能枠の取得で候補が狭まったとき、選択中の日付が候補から消えたままにならないよう寄せ直す
  React.useEffect(() => {
    if (dateOptions.length === 0) return;
    if (!dateOptions.some((option) => option.value === firstPreferredDate)) {
      setFirstPreferredDate(dateOptions[0].value);
    }
    if (!dateOptions.some((option) => option.value === secondPreferredDate)) {
      setSecondPreferredDate((dateOptions[1] ?? dateOptions[0]).value);
    }
  }, [dateOptions, firstPreferredDate, secondPreferredDate]);

  React.useEffect(() => {
    const isSelectedTimeSlotAvailable = firstTimeSlotOptions.some((option) => option.value === firstPreferredTimeSlot);
    const nextTimeSlot = firstTimeSlotOptions[0]?.value;
    if (!isSelectedTimeSlotAvailable && nextTimeSlot) {
      setFirstPreferredTimeSlot(nextTimeSlot);
    }
  }, [firstPreferredTimeSlot, firstTimeSlotOptions]);

  React.useEffect(() => {
    const isSelectedTimeSlotAvailable = secondTimeSlotOptions.some(
      (option) => option.value === secondPreferredTimeSlot
    );
    const nextTimeSlot = secondTimeSlotOptions[0]?.value;
    if (!isSelectedTimeSlotAvailable && nextTimeSlot) {
      setSecondPreferredTimeSlot(nextTimeSlot);
    }
  }, [secondPreferredTimeSlot, secondTimeSlotOptions]);

  const handleClick = React.useCallback(() => {
    if (variant === "planner" && submittingRef.current) return;
    if (variant !== "planner") document.body.style.overflow = "auto"; // Enable scrolling
    onClose();
  }, [onClose, variant]);

  const showRecommendationsOrClose = React.useCallback(() => {
    if (submittingRef.current) return;
    if (additionalAgents.length > 0) {
      setIsRecommendationOnlyVisible(true);
      return;
    }

    handleClick();
  }, [additionalAgents.length, handleClick]);

  const handleSubmitPreferredContactSchedules = React.useCallback(async () => {
    if (submittingRef.current) return;
    // 送信ハンドラが渡っていない呼び出し元では日程を保存できないので、スキップと同じ扱いで進む
    if (!onSubmitPreferredContactSchedules) {
      showRecommendationsOrClose();
      return;
    }

    // 候補が 1 件も無い（＝初期化前）状態での空送信を防ぐ。ボタンも同条件で disabled にしている
    if (!firstPreferredDate || !firstPreferredTimeSlot || !secondPreferredDate || !secondPreferredTimeSlot) return;

    submittingRef.current = true;
    setIsSubmitting(true);
    let ok = false;
    try {
      ok = await onSubmitPreferredContactSchedules({
        first_preferred_contact_date: firstPreferredDate,
        first_preferred_contact_time_slot:
          firstPreferredTimeSlot as InvestmentProposalPreferredContactSchedulesRequestFirstPreferredContactTimeSlotEnum,
        second_preferred_contact_date: secondPreferredDate,
        second_preferred_contact_time_slot:
          secondPreferredTimeSlot as InvestmentProposalPreferredContactSchedulesRequestSecondPreferredContactTimeSlotEnum,
      });
    } catch {
      ok = false;
    } finally {
      submittingRef.current = false;
      if (mountedRef.current) setIsSubmitting(false);
    }
    if (!mountedRef.current) return;

    if (ok) {
      showRecommendationsOrClose();
      return;
    }

    // 失敗してもモーダルは開いたままにして、再送信かスキップかをユーザーに委ねる。
    // Provider が preventDuplicate なので、同じ文言の2回目以降が握り潰される。
    // 連続して失敗したときも毎回伝えたいので、この通知だけ重複抑止を外す
    enqueueSnackbar(PREFERRED_CONTACT_SCHEDULES_ERROR_MESSAGE, { preventDuplicate: false });
  }, [
    enqueueSnackbar,
    firstPreferredDate,
    firstPreferredTimeSlot,
    onSubmitPreferredContactSchedules,
    secondPreferredDate,
    secondPreferredTimeSlot,
    showRecommendationsOrClose,
  ]);

  // 「閉じる」を出す条件。日程フォームを出さないオファー（msins 系）と、
  // おすすめ表示に切り替わった後。日程フォーム中はスキップ導線が出口を兼ね、
  // 対象外の案内中は「完了」ボタンが出口を兼ねる
  const isCloseLinkVisible =
    (!isPreferredContactScheduleVisible && !isOutOfTargetGuidanceVisible) || isRecommendationOnlyVisible;

  // 期限（旧文言の「2営業日以内」）は約束しない。希望日程を入力してもらう建て付けのため、
  // 期限の確約は入力された日程と矛盾して見える（例: 6日後を指定）ことがある。
  // 電話が来ること自体は、日程をスキップしたユーザーにも伝わるよう説明文に置く。
  // 3タイプで共通の文言なので1箇所で定義し、変更時に片側だけ残らないようにする
  const scheduleOfferText = {
    title: "マッチングが成立しました！",
    description: "認定プランナーよりお電話にてご連絡いたします。ご都合のよい日時を教えてください。",
  };
  const offerTextMap = {
    // 対象条件内のユーザーオファーは成立済みとして案内する。承諾待ちは別の文言に切り替える。
    user_offer: scheduleOfferText,
    investment: scheduleOfferText,
    assessment: scheduleOfferText,
    msins_own: {
      title: "ZOOM面談依頼を承りました",
      description: `ご興味をお持ちいただきありがとうございます。
      提携の三井住友インシュアランス＆フィナンシャルサービスより、ZOOM個別面談のご案内（メール）が届きますので、日程の調整をお願いします。`,
    },
    msins_consideration: {
      title: "ZOOM面談依頼を承りました",
      description: `ご興味をお持ちいただきありがとうございます。
      提携の三井住友インシュアランス＆フィナンシャルサービスより、ZOOM個別面談のご案内（メール）が届きますので、日程の調整をお願いします。`,
    },
  };

  // 対象外はまだマッチが承諾されていないため、「成立しました」の文言・握手イラストは使わない
  const currentText = isOutOfTargetGuidanceVisible
    ? { title: OUT_OF_TARGET_CONDITION_TITLE, description: OUT_OF_TARGET_CONDITION_DESCRIPTION }
    : offerTextMap[offerType] || offerTextMap["investment"]; // デフォルト保険

  // スクロール制御
  React.useEffect(() => {
    // プランナーの画面は日程選択・おすすめシートと同じ MUI の制御に任せる。
    if (variant === "planner") return;
    document.body.style.overflow = "hidden"; // Disable scroll when modal is open
    return () => {
      document.body.style.overflow = "auto"; // Enable scroll when modal is unmounted
    };
  }, [variant]);

  if (variant === "planner")
    return (
      <PlannerApprovalPresentation
        title={currentText.title}
        description={currentText.description}
        recommendationOnly={isRecommendationOnlyVisible}
        additionalAgents={additionalAgents}
        onClickAdditionalAgent={onClickAdditionalAgent}
        onClose={handleClick}
        onComplete={showRecommendationsOrClose}
        onSkip={showRecommendationsOrClose}
        onSubmit={() => void handleSubmitPreferredContactSchedules()}
        isSubmitting={isSubmitting}
        canSubmit={canSubmitPreferredContactSchedules}
        showSchedule={isPreferredContactScheduleVisible}
        dateOptions={dateOptions}
        firstDate={firstPreferredDate}
        secondDate={secondPreferredDate}
        firstSlot={firstPreferredTimeSlot}
        secondSlot={secondPreferredTimeSlot}
        firstSlotOptions={firstTimeSlotOptions}
        secondSlotOptions={secondTimeSlotOptions}
        onFirstDate={setFirstPreferredDate}
        onSecondDate={setSecondPreferredDate}
        onFirstSlot={(value) => setFirstPreferredTimeSlot(value as PreferredContactTimeSlot)}
        onSecondSlot={(value) => setSecondPreferredTimeSlot(value as PreferredContactTimeSlot)}
      />
    );

  return (
    <StyledModal onClickOutside={handleClick}>
      <Box className={classes.messageContent}>
        {!isRecommendationOnlyVisible && (
          <>
            {/* イラスト（対象外は成立を示す握手ではなく、申請済みを示すイラストにする） */}
            {isOutOfTargetGuidanceVisible ? (
              <img src={MatchingCheckImage} alt="マッチ申請" className={classes.image} />
            ) : (
              <img src={ShakeHandsImage} alt="ShakeHands" className={classes.image} />
            )}

            <Box textAlign="center">
              <Typography className={classes.title}>{currentText.title}</Typography>
            </Box>

            <Typography
              className={`${classes.description} ${
                isPreferredContactScheduleVisible ? classes.investmentDescription : ""
              }`}
            >
              {currentText.description.split("\n").map((line, index) => (
                <React.Fragment key={index}>
                  {line}
                  <br />
                </React.Fragment>
              ))}
            </Typography>
          </>
        )}
        {isPreferredContactScheduleVisible && !isRecommendationOnlyVisible && (
          <Typography className={classes.investmentDescriptionNote}>※1週間以内の日付を指定してください</Typography>
        )}
        {/* 対象外は日程を選ばせず、この「完了」だけでマッチ申請を終えられるようにする */}
        {isOutOfTargetGuidanceVisible && !isRecommendationOnlyVisible && (
          <Box className={classes.submitButtonWrap}>
            <CustomButton customVariant="filled" onClick={showRecommendationsOrClose}>
              {OUT_OF_TARGET_CONDITION_SUBMIT_LABEL}
            </CustomButton>
          </Box>
        )}
        {isPreferredContactScheduleVisible && !isRecommendationOnlyVisible && (
          <Box className={classes.scheduleForm}>
            <Box className={classes.scheduleFieldGroup}>
              <Typography className={classes.scheduleLabel}>第1希望日</Typography>
              <Box className={classes.scheduleRow}>
                <Select
                  variant="outlined"
                  value={firstPreferredDate}
                  onChange={(e) => setFirstPreferredDate(e.target.value as string)}
                  className={classes.scheduleSelect}
                  MenuProps={selectMenuProps}
                >
                  {dateOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                <Select
                  variant="outlined"
                  value={firstPreferredTimeSlot}
                  onChange={(e) => setFirstPreferredTimeSlot(e.target.value as PreferredContactTimeSlot)}
                  className={classes.scheduleSelect}
                  MenuProps={selectMenuProps}
                >
                  {firstTimeSlotOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            </Box>
            <Box className={classes.scheduleFieldGroup}>
              <Typography className={classes.scheduleLabel}>第2希望日</Typography>
              <Box className={classes.scheduleRow}>
                <Select
                  variant="outlined"
                  value={secondPreferredDate}
                  onChange={(e) => setSecondPreferredDate(e.target.value as string)}
                  className={classes.scheduleSelect}
                  MenuProps={selectMenuProps}
                >
                  {dateOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                <Select
                  variant="outlined"
                  value={secondPreferredTimeSlot}
                  onChange={(e) => setSecondPreferredTimeSlot(e.target.value as PreferredContactTimeSlot)}
                  className={classes.scheduleSelect}
                  MenuProps={selectMenuProps}
                >
                  {secondTimeSlotOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            </Box>
            <Box className={classes.submitButtonWrap}>
              <CustomButton
                customVariant="filled"
                onClick={handleSubmitPreferredContactSchedules}
                disabled={isSubmitting || !canSubmitPreferredContactSchedules}
              >
                送信して完了
              </CustomButton>
            </Box>
            <Box className={classes.skipButtonContainer}>
              <Typography
                variant="button"
                color="primary"
                className={`${classes.link} ${classes.button}`}
                onClick={showRecommendationsOrClose}
              >
                スキップ
              </Typography>
            </Box>
            <Typography className={classes.noteText}>
              ご入力の日程をもとに、認定プランナーよりご連絡いたします。なお、連絡先は「認定プランナー情報」からもご確認いただけます。
            </Typography>
          </Box>
        )}
        {/* おすすめプランナーは日程送信の後に出す（送信前に別プランナーへ誘導すると日程入力が離脱する） */}
        {isRecommendationOnlyVisible && additionalAgents.length > 0 && (
          <Box className={classes.recommendationSection}>
            <img src={RecommendAgentImage} alt="おすすめの認定プランナー" className={classes.recommendationImage} />
            <Typography className={classes.recommendationTitle}>あなたにオススメの認定プランナー</Typography>
            <Typography className={classes.recommendationTopText}>
              この認定プランナーに話を聞いた方の多くが、以下の認定プランナーにも話を聞いています。
            </Typography>
            <Box className={classes.recommendationList}>
              {additionalAgents.slice(0, 3).map((agent) => (
                <Box
                  key={agent.id}
                  className={classes.recommendationCard}
                  role="button"
                  tabIndex={0}
                  onClick={() => onClickAdditionalAgent?.(agent)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onClickAdditionalAgent?.(agent);
                    }
                  }}
                >
                  <Box className={classes.recommendationProfileWrap}>
                    <span className={classes.recommendationProfileBgCircle} />
                    <Avatar
                      src={agent.agent_profile_image?.image_url || NoImage}
                      alt={`${agent?.family_name ?? ""}${agent?.first_name ?? ""}`}
                      className={classes.recommendationAvatar}
                    />
                  </Box>
                  <Typography className={`${classes.recommendationCompany} ${classes.recommendationNameOneLine}`}>
                    {agent.company?.name || "-"}
                  </Typography>
                  <Typography className={`${classes.recommendationName} ${classes.recommendationNameOneLine}`}>
                    {`${agent?.family_name ?? ""}${agent?.first_name ?? ""}`}
                  </Typography>
                  <Box className={classes.recommendationButton}>
                    <CustomButton
                      customVariant="filledForAgentSearchFit"
                      onClick={(e) => {
                        e.stopPropagation();
                        onClickAdditionalAgent?.(agent);
                      }}
                      size="small"
                    >
                      詳細を見る
                    </CustomButton>
                  </Box>
                </Box>
              ))}
            </Box>
            <Typography className={classes.recommendationBottomText}>
              満足度の高い不動産投資を行っている方の多くは、2〜3社から話を聞き、提案内容を比較した上で判断しています。
            </Typography>
          </Box>
        )}
        {isCloseLinkVisible && (
          <Box className={classes.buttonContainer}>
            <Typography
              variant="button"
              color="primary"
              className={`${classes.link} ${classes.button}`}
              onClick={handleClick}
            >
              閉じる
            </Typography>
          </Box>
        )}
      </Box>
    </StyledModal>
  );
};
